import { CircularProgress, Tooltip } from '@mui/material';
import { loadDataSource, runSource, setDetailViewCard } from 'js/actions';
import { useExpandedNodes } from 'js/components/pipelines/FlowNodes/context/ExpandContext';
import {
  GET_FLOW_RUN_SUMMARY_REQUEST,
  RUN_SOURCE_NOW_REQUEST,
  UPDATE_SOURCE_INFO_REQUEST,
} from 'js/constants/actionTypes';
import {
  CONNECTION_CATEGORY_TEMPLATIZED_API,
  CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
  CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT,
  CONNECTION_CATEGORY_WEBHOOK,
} from 'js/constants/connectionCategories';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { ACLQueryModeTypes } from 'js/constants/visibilityFilters';
import useAppDispatch from 'js/hooks/useAppDispatch';
import DateUtils from 'js/lib/DateUtils';
import {
  getConnectionOrVendorType,
  getResourceApiKeys,
  isSqlAutomation,
} from 'js/lib/ResourceUtils';
import UIUtils from 'js/lib/UIUtils';
import { getUsableApiKeyEntity, getWebhookUrl, sanitizeApiKeyURL } from 'js/lib/URLUtils';
import { ReactComponent as BoltIcon } from 'new/components/icons/fi-rr-bolt.svg';
import { ReactComponent as ClockIcon } from 'new/components/icons/fi-rr-clock.svg';
import { ReactComponent as PlayIcon } from 'new/components/icons/fi-rr-play.svg';
import { ChipWithCopy } from 'new/elements/ChipWithCopy/ChipWithCopy';
import { resourceTypes } from 'new/elements/ResourceSearch/ResourceSearch';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { useDisplayFlowRunNow } from 'new/hooks/useFeatureFlags';
import { useAppContext } from 'new/hooks/useAppContext';
import usePaging from 'new/state/paging/repository/usePaging';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useExternalTrigger } from './context/ExternalTriggerContext';
import CronScheduleDialog from './CronScheduleDialog';
import classes from './ExpandActions.module.scss';
import { useDrawLines } from './hooks/useDrawLines';
import {
  useFlowRunSummary,
  useResourceApiKeys,
  useUpdateConfig,
} from './hooks/useExpandActionsEffects';
import { renderNextRunTime } from './utils/renderNextRunTime';
import {
  shouldRenderCronAction,
  shouldRenderExternalTriggerAction,
  shouldRenderRunAction,
  shouldRenderWebhookAction,
} from './utils/shouldRenderSourceAction';
import { FlowTriggerHelper } from 'js/components/data_sources/add_ds_form/MonitoringInterval/FlowTriggerHelper';

const ACTIONS_EXPANDED_ATTR = 'data-actions-expanded';

const makeGetResourceApiKeysSelector = () =>
  createSelector(
    (state: any) => state.entities.apiKeys,
    (_, resourceData) => resourceData,
    (apiKeysState, resourceData) =>
      getResourceApiKeys({ id: resourceData?.id, api_keys: resourceData?.api_keys }, apiKeysState),
  );

interface ActionItem {
  id: string;
  text?: string;
  icon: React.ReactNode;
  iconText?: string;
  onClick?: (e?: React.MouseEvent) => void;
  iconTooltip: string;
  tooltipCopyValue?: string;
  copiedTooltip: string;
  subText?: string;
  isVisible?: boolean;
  disabled?: boolean;
}

export const ExpandActions = ({
  resourceType,
  resourceId,
  data,
  isVisible = true,
  canvasScheduleControlsActive,
}: {
  resourceType: (typeof resourceTypes)[number]['value'];
  resourceId: string;
  data?: any;
  isVisible?: boolean;
  canvasScheduleControlsActive?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { mode, isEmbeddedMode } = useAppContext();
  const { onExpandSource, expandedSources } = useExpandedNodes();
  const { openPortal } = useExternalTrigger();
  const { paging } = usePaging();
  const vendors = useSelector((s: any) => s.entities.vendors);
  const connectionOrVendorType = getConnectionOrVendorType(
    data,
    vendors,
    'source_type',
    'vendor_id',
  );
  const behaviorType = isSqlAutomation(data, vendors) ? 'sql_runner' : connectionOrVendorType;
  const connectionInfo = paging.metaInfoById?.[behaviorType] || null;
  const connectionCategory = connectionInfo?.connectionCategory || null;
  // For templatized API, use template_config only if it has the cron value
  // (Advanced Mode sources have cron in source_config instead)
  //TODO: This is temporary solution. Ideally API should have guaranteed return of vendor_endpoint_id in flow repsonse.data_source. Checking on vendor_endpoint_id would be the fool proof solution.
  const config =
    [CONNECTION_CATEGORY_TEMPLATIZED_API, CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI].includes(
      connectionCategory,
    ) && data?.template_config?.['start.cron']
      ? data?.template_config
      : data?.source_config;
  const currentOrg = useSelector((state: any) => state.app.currentOrg);
  const getResourceApiKeysSelector = React.useMemo(makeGetResourceApiKeysSelector, []);
  const apiKeys = useSelector((state: any) => getResourceApiKeysSelector(state, data));
  const flowRunSummary = useSelector((state: any) => state.metrics.flowRunSummary);
  const isWebhookSource = connectionCategory === CONNECTION_CATEGORY_WEBHOOK;
  const isExternalTriggerCompatibleSource = data?.['adaptive_flow'] === true;
  const [actionsExpanded, setActionsExpanded] = useState(expandedSources[data.flow_node_id]);
  const [showCronModal, setShowCronModal] = useState(false);
  const [newConfig, setNewConfig] = useState(config || {});
  const [cronAnchorEl, setCronAnchorEl] = useState<HTMLElement | null>(null);

  const expandButtonRef = useRef<HTMLDivElement>(null);
  const actionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDataSourceLoaded = useRef(false);
  const isMounted = useRef(false);

  const isSourceProcessing = data?.runtime_status === 'PROCESSING' || false;
  const isRunNowLoading = useIsLoading(RUN_SOURCE_NOW_REQUEST);
  const isUpdateSourceLoading = useIsLoading(UPDATE_SOURCE_INFO_REQUEST);
  const isFlowRunSummaryLoading = useIsLoading(GET_FLOW_RUN_SUMMARY_REQUEST);
  const isRunNowDisabled =
    isSourceProcessing || isRunNowLoading || data.ingestion_mode === 'sampling';

  useLayoutEffect(() => {
    // Only expand on initial mount if not already expanded
    if (!actionsExpanded && !isMounted.current) {
      const timeoutId = setTimeout(() => {
        expandButtonRef.current?.click();
      }, 100);
      isMounted.current = true;

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, []);

  useResourceApiKeys(resourceType, data, apiKeys, actionsExpanded);
  useFlowRunSummary(resourceType, data, flowRunSummary, actionsExpanded);

  const { handleUpdateConfig } = useUpdateConfig(resourceId);

  const usableApiKey = getUsableApiKeyEntity(apiKeys);
  const cronExpression = config?.['start.cron'];

  const webhookUrl =
    usableApiKey && 'url' in usableApiKey
      ? usableApiKey.url
      : getWebhookUrl(resourceId, currentOrg, apiKeys);

  const sanitizedWebhookUrl = webhookUrl ? sanitizeApiKeyURL(webhookUrl) : null;

  const samplePayload = UIUtils.getWebhookSampleRequest(
    webhookUrl,
    sanitizedWebhookUrl,
    config,
    'application/json',
    `{\n//Add your payload here... \n}`,
  );

  const paths = useDrawLines(
    actionsExpanded,
    expandButtonRef,
    actionRefs,
    containerRef,
    classes.iconWrapper,
    300,
  );

  const { data: summaryData } = flowRunSummary[data.nodeKey] || {};
  const summaryRunIds = Object.keys(summaryData || {}).sort((a, b) => parseInt(b) - parseInt(a));

  const lastRunId = data?.last_run_id != null ? String(data.last_run_id) : summaryRunIds[0];
  const formattedLastRun = lastRunId
    ? DateUtils.getDateString(new Date(parseInt(lastRunId)), 'MMM Do, YYYY [at] h:mm A z')
    : 'No previous runs';

  const handleRunNow = () => {
    dispatch(runSource(data.data_source_id));
  };

  const handleRunCronJob = (e: React.MouseEvent) => {
    e?.stopPropagation();

    if (!cronExpression) return;

    setNewConfig(config || {});
    setShowCronModal(true);
    setCronAnchorEl(e.currentTarget as HTMLElement);
  };

  const handleClosePopover = () => {
    setShowCronModal(false);
    setCronAnchorEl(null);
  };

  const handleUpdateConfigWithUI = () => {
    handleUpdateConfig(newConfig, data, connectionCategory, () => {
      setShowCronModal(false);
      setCronAnchorEl(null);
    });
  };

  const handleConfigChange = (data: any, fieldName: string, value: any) => {
    setNewConfig({
      ...data,
      [fieldName]: value,
    });
  };

  const handleLastRunClick = () => {
    dispatch(setDetailViewCard(data, 'Insights'));
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onExpandSource(data.flow_node_id);
    setActionsExpanded(!actionsExpanded);

    if (!isDataSourceLoaded.current && isWebhookSource) {
      dispatch(
        loadDataSource(resourceId, {
          aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.key,
        }),
      ).catch((e) => {
        isDataSourceLoaded.current = false;
      });

      isDataSourceLoaded.current = true;
    }
  };

  const isCronOnSinkCompletion = FlowTriggerHelper.getInboundTriggers(data).some(
    (trigger) => trigger.triggering_resource_type === 'data_sink',
  );

  const isCronOnSourceCompletion =
    // Only triggers that START this source; its own outbound ones (it triggers another
    // flow) must not label it "On Source Completion" (NEX-22548).
    FlowTriggerHelper.getInboundTriggers(data).some(
      (trigger) => trigger.triggering_resource_type === 'data_source',
    );

  /** When true, Run Now / Cron are canvas nodes — hide duplicate UI from this strip */
  const canvasFlowControlsEnabled = useDisplayFlowRunNow();
  const flowRootId = data?.origin_node_id as number | undefined;
  const flowRootEntity = useSelector((state: any) =>
    flowRootId != null ? state.entities.flowNodes?.[flowRootId] : undefined,
  );
  const resolvedFlowType = flowRootEntity?.flow_type ?? data?.flow_type;
  const resolvedExcludeCanvasControls = !!(
    flowRootEntity?.excludeFlowControlNode ?? data?.excludeFlowControlNode
  );
  const isSamplingMode =
    flowRootEntity?.ingestion_mode === 'sampling' || data?.ingestion_mode === 'sampling';
  const isEmailScript = connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT;
  const hasCronConfig = !!data?.source_config?.['start.cron'];
  const isRagFlow = resolvedFlowType === 'rag' || data?.flow_type === 'rag';

  const stripDefersRunCronToCanvas =
    !!canvasScheduleControlsActive ||
    (canvasFlowControlsEnabled && !isRagFlow && !resolvedExcludeCanvasControls);

  const cronRenderedOnCanvas =
    stripDefersRunCronToCanvas && !isSamplingMode && !isEmailScript && hasCronConfig;

  const runRenderedOnCanvas =
    stripDefersRunCronToCanvas &&
    !isSamplingMode &&
    shouldRenderRunAction(data, connectionCategory);

  const items: ActionItem[] = [
    ...(shouldRenderWebhookAction(data)
      ? [
          {
            id: 'webhook',
            text: 'WEBHOOK',
            icon: <BoltIcon />,
            iconText: null,
            onClick: () => {},
            iconTooltip: 'Click to copy webhook request with sample payload',
            tooltipCopyValue: samplePayload?.sample,
            copiedTooltip: 'Webhook request with sample payload copied',
          },
        ]
      : []),
    ...(shouldRenderExternalTriggerAction(data)
      ? [
          {
            id: 'external_trigger',
            text: 'EXTERNAL TRIGGER',
            icon: <BoltIcon />,
            iconText: null,
            onClick: (e) => {
              e.stopPropagation();
              openPortal(data);
            },
            iconTooltip: 'Click to view external trigger documentation',
            copiedTooltip: null,
          },
        ]
      : []),
    ...(shouldRenderCronAction(data, connectionCategory) && !cronRenderedOnCanvas
      ? [
          {
            id: 'cron',
            text: 'CRON JOB',
            icon: <ClockIcon />,
            iconText: null,
            subText: `Next Run\n${
              isCronOnSinkCompletion
                ? `On Destination Completion\n${renderNextRunTime(cronExpression)}`
                : isCronOnSourceCompletion
                  ? `On Source Completion\n${renderNextRunTime(cronExpression)}`
                  : renderNextRunTime(cronExpression)
            }`,
            onClick: (e) => {
              handleRunCronJob(e);
            },
            iconTooltip: null,
            copiedTooltip: null,
          },
        ]
      : []),
    ...(shouldRenderRunAction(data, connectionCategory) && !runRenderedOnCanvas
      ? [
          {
            id: 'run',
            text: null,
            icon: isRunNowLoading ? <CircularProgress size={16} color="primary" /> : <PlayIcon />,
            iconText: !isRunNowLoading ? 'RUN NOW' : null,
            subText: `Last Run\n${isFlowRunSummaryLoading ? 'Loading...' : formattedLastRun}`,
            onClick: !isRunNowDisabled ? handleRunNow : undefined,
            disabled: isRunNowDisabled,
            iconTooltip: isSourceProcessing
              ? 'Unavailable - Source is currently processing'
              : data.ingestion_mode === 'sampling'
                ? 'To ingest data, please switch Sampling Mode of the source'
                : null,
            copiedTooltip: null,
          },
        ]
      : []),
  ];

  const isSourceAutoGenerated = data?.auto_generated;

  if (resourceType !== RESOURCE_TYPE_SOURCE || isSourceAutoGenerated || items.length === 0) {
    return null;
  }

  const renderConditionalTooltipWithCopy = (item: ActionItem) => {
    if (item.copiedTooltip) {
      return (
        <ChipWithCopy
          toCopy={item.tooltipCopyValue}
          tooltipTitle={item.iconTooltip}
          tooltipCopiedTitle={item.copiedTooltip}
          mode="light"
          placement="top-start"
        >
          <div className={classes.iconWrapper}>
            <div className={classes.actionIcon}>{item.icon}</div>
          </div>
        </ChipWithCopy>
      );
    }

    return null;
  };

  // Take up the room of the expand actions container even if the actions do not have to be displayed so that other elements in the DAG do not jump
  if (!isVisible) return <div className={classes.expandActionsContainer} />;

  return (
    <div
      ref={containerRef}
      className={classes.expandActionsContainer}
      data-flow-node-id={data?.flow_node_id}
      data-actions-only-expansion="true"
      {...{ [ACTIONS_EXPANDED_ATTR]: actionsExpanded ? 'true' : 'false' }}
    >
      {actionsExpanded && paths.length > 0 && (
        <svg className={classes.linesContainer}>
          {paths.map((path, index) => (
            <path
              key={`connection-${index}`}
              d={path}
              className={classes.connectionLine}
              strokeWidth="2"
              fill="none"
              stroke="#000"
            />
          ))}
        </svg>
      )}

      <Tooltip title={actionsExpanded ? 'Hide nodes' : 'Flow Triggers'} placement="bottom">
        <div
          ref={expandButtonRef}
          className={`${classes.expandButton} ${actionsExpanded ? classes.expandButtonActive : ''}`}
          onClick={handleExpandClick}
          data-testid="expand-button"
        >
          <div className={classes.expandButtonIcon}>‹</div>
        </div>
      </Tooltip>

      {actionsExpanded && (
        <div className={classes.expandActionsContent}>
          {items.map((item, index) => (
            <div key={item.id} className={classes.actionWrapper}>
              <div ref={(el) => (actionRefs.current[index] = el)} className={classes.actionItem}>
                {renderConditionalTooltipWithCopy(item) || (
                  <Tooltip title={item.iconTooltip} placement="top" arrow>
                    <div
                      className={`${classes.iconWrapper} ${
                        item.id === 'run' ? classes.runIconWrapper : ''
                      }
                      ${item.id === 'run' && isRunNowLoading ? classes.runIconWrapperLoading : ''}
                      ${item.disabled ? classes.iconWrapperDisabled : ''}
                      `}
                      onClick={(e) => {
                        if (!item.disabled) {
                          item?.onClick?.(e);
                        }
                      }}
                    >
                      <div className={classes.actionIcon}>{item.icon}</div>
                      {item.iconText && <span className={classes.iconText}>{item.iconText}</span>}
                    </div>
                  </Tooltip>
                )}

                {item.id === 'cron' && (
                  <CronScheduleDialog
                    open={showCronModal}
                    onClose={handleClosePopover}
                    config={newConfig}
                    onConfigChanged={handleConfigChange}
                    onUpdate={handleUpdateConfigWithUI}
                    anchorEl={cronAnchorEl}
                    isLoading={isUpdateSourceLoading}
                    source={data}
                    showExternalTrigger={isExternalTriggerCompatibleSource}
                  />
                )}

                <div className={classes.textContainer}>
                  {item.text && <div className={classes.actionLabel}>{item.text}</div>}
                  {item.subText && (
                    <div
                      className={`${isEmbeddedMode && mode === 'dark' ? classes.actionSubTextDark : classes.actionSubText} ${
                        item.id === 'run' || item.id === 'cron' ? classes.actionSubTextRun : ''
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item.id === 'run') {
                          handleLastRunClick();
                          return;
                        }
                        if (item.id === 'cron') {
                          handleRunCronJob(e);
                          return;
                        }
                      }}
                    >
                      {item.subText.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
